/**
 * Generic Privacy Policy Page for City Landing Pages
 *
 * Parameterized privacy policy component that works for any city.
 * Updates city name, state, and contact info dynamically.
 *
 * Usage:
 *   <PrivacyPolicyPage
 *     cityName="Houston"
 *     stateName="Texas"
 *     cityDomain="houston.tx.toolboxlogic.com"
 *     effectiveDate="November 22, 2025"
 *   />
 */

import React from 'react';
import { useEffect } from 'react';
import { Card } from '../../src/components/ui/card';

export interface PrivacyPolicyPageProps {
  cityName: string;
  stateName?: string;
  cityDomain: string; // e.g., "houston.tx.toolboxlogic.com"
  effectiveDate?: string; // Default: current date
  companyName?: string; // Default: "Capetian XX LLC dba ToolboxLogic"
  contactEmail?: string; // Default: "privacy@toolboxlogic.com"
}

export function PrivacyPolicyPage({
  cityName,
  stateName = '',
  cityDomain,
  effectiveDate,
  companyName = 'Capetian XX LLC, a Nevada limited liability company doing business as ToolboxLogic',
  contactEmail = '' /* 'privacy@toolboxlogic.com' */,
}: PrivacyPolicyPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const displayDate =
    effectiveDate ||
    new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const cityStateDisplay = stateName ? `${cityName}, ${stateName}` : cityName;

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-muted/40">
      <div className="container max-w-3xl space-y-4 sm:space-y-6 px-4 sm:px-6">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
          Privacy Policy
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Effective {displayDate}</p>

        <p className="text-sm sm:text-base leading-relaxed">
          This Privacy Policy explains how {companyName} ("ToolboxLogic," "we," "us," or "our"),
          collects, uses, and protects personal information when you visit the {cityName} pilot
          landing page or submit interest forms (collectively, the "Services").
        </p>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            1. Information We Collect
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We collect the following categories of information:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>
              <strong>Personal Information:</strong> Name, email address, phone number, ZIP code (if
              provided voluntarily).
            </li>
            <li>
              <strong>Community Interests:</strong> Preferences for platform features, participation
              in champions program, comments or feedback.
            </li>
            <li>
              <strong>Technical Information:</strong> IP address, browser type, device information,
              and anonymized usage data (via analytics cookies).
            </li>
            <li>
              <strong>Optional Information:</strong> Facebook groups you belong to (if provided
              voluntarily to gauge community network reach).
            </li>
          </ul>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            2. How We Use Your Information
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We use collected information for the following purposes:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>
              <strong>Interest Gauging:</strong> To assess demand for a community platform in{' '}
              {cityStateDisplay}.
            </li>
            <li>
              <strong>Communication:</strong> To send updates about platform development, launch
              timelines, and pilot opportunities (you can unsubscribe at any time).
            </li>
            <li>
              <strong>Champions Program:</strong> To identify and recruit early testers who can
              provide feedback and help shape the platform.
            </li>
            <li>
              <strong>Analytics:</strong> To analyze website traffic, user behavior, and optimize
              the landing page experience.
            </li>
            <li>
              <strong>Compliance:</strong> To meet legal obligations and protect against fraud or
              abuse.
            </li>
          </ul>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            3. Sharing Your Information
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We <strong>do not sell</strong> your personal information. We may share your information
            with:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>
              <strong>Service Providers:</strong> Third-party vendors who assist with email
              delivery, analytics, and hosting (e.g., Google Analytics, Mailchimp, Cloudflare).
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law, subpoena, or to protect our
              legal rights.
            </li>
            <li>
              <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of
              assets, your information may be transferred to the new entity.
            </li>
          </ul>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">4. Data Retention</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We retain your personal information for as long as necessary to fulfill the purposes
            outlined in this policy, or as required by law. If you request deletion, we will remove
            your data within 30 days unless retention is legally required.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">5. Your Rights</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>
              <strong>Access:</strong> Request a copy of the personal information we hold about you.
            </li>
            <li>
              <strong>Correction:</strong> Request corrections to inaccurate or incomplete
              information.
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your personal information (subject to
              legal exceptions).
            </li>
            <li>
              <strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time via the
              "unsubscribe" link.
            </li>
          </ul>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            6. How to Exercise Your Rights
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            To request access to, correction of, or deletion of your personal data, please use our{' '}
            <a href="/contact" className="text-primary hover:underline font-medium">
              Contact Form
            </a>
            .
          </p>
          <p className="text-sm sm:text-base leading-relaxed">
            We will respond to your request within 30 days as required by applicable privacy laws
            (GDPR, CCPA, etc.).
          </p>
          <p className="text-sm sm:text-base leading-relaxed">
            For email unsubscription, click the "Unsubscribe" link in any email we send you, or
            visit our Unsubscribe page (coming soon).
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            7. Cookies and Tracking
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We use cookies and similar tracking technologies to analyze site usage and improve user
            experience. You can disable cookies in your browser settings, but this may limit certain
            website functionality.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">7. Security</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We implement industry-standard security measures to protect your personal information
            from unauthorized access, disclosure, or destruction. However, no internet transmission
            is 100% secure, and we cannot guarantee absolute security.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">8. Children's Privacy</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            Our Services are not directed to individuals under 18 years of age. We do not knowingly
            collect personal information from children. If we discover that a child has provided us
            with personal information, we will delete it immediately.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            10. Changes to This Policy
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We may update this Privacy Policy from time to time. Changes will be posted on this page
            with an updated "Effective Date." Your continued use of the Services after changes
            constitutes acceptance of the updated policy.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">11. Contact Us</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            If you have questions about this Privacy Policy or wish to exercise your rights, please
            visit our{' '}
            <a href="/contact" className="text-primary hover:underline font-medium">
              Contact Page
            </a>{' '}
            to submit an inquiry.
          </p>
          <p className="text-sm sm:text-base leading-relaxed">
            <strong>ToolboxLogic</strong>
            <br />
            Community Engagement Platform
          </p>
        </div>
      </div>
    </section>
  );
}

export default PrivacyPolicyPage;
